import React, { createContext, useCallback, useContext, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import ModalError from "~/components/common/ModalError";
import { getApiErrorConfig, extractApiMessage, APPOINTMENT_LIMIT_MODAL_IDS } from "~/utils/apiErrorMessages";
import { mixpanelTrackEvent } from "~/utils/mixpanel";
import Utils from "~/helpers/Utils";
import dayjs from "~/config/dayjs";

const ApiErrorModalContext = createContext(null);

const EMPTY_STATE = {
  open: false,
  title: "",
  message: null,
  children: null,
  primaryButton: null,
  secondaryButton: null,
};

export function ApiErrorModalProvider({ children }) {
  const { t, i18n } = useTranslation();
  const { business } = useSelector((store) => store.business);
  const meStore = useSelector((store) => store.me?.me);
  const currentUser = Utils.safeParseJSON("@prit-web/meData", meStore);
  const [modal, setModal] = useState(EMPTY_STATE);

  const hideError = useCallback(() => {
    setModal((prev) => ({ ...prev, open: false }));
  }, []);

  const resolveAction = useCallback(
    (action) => {
      if (action === "close" || !action) return hideError;
      return () => {
        action();
        hideError();
      };
    },
    [hideError]
  );

  /**
   * Formata os detalhes dos serviços para a mensagem do WhatsApp.
   */
  const formatServicesInfo = useCallback(
    (services) => {
      if (!services?.length) return "";
      const labels = {
        service: t("appointmentLimitServiceLabel"),
        date: t("appointmentLimitDateLabel"),
        time: t("appointmentLimitTimeLabel"),
        professional: t("appointmentLimitProfessionalLabel"),
      };
      const divider = "\n-------------------------\n";
      return services
        .map((svc) => {
          const dateStr = i18n.language !== "en-US"
            ? dayjs(svc?.date).format("DD/MM/YYYY")
            : dayjs(svc?.date).format("MM/DD/YYYY");
          const hourStr = dayjs(svc?.date).utc().tz(business?.timeZone).format("HH:mm");
          return [
            `${labels.service}: ${svc?.service?.serviceName ?? "-"}`,
            `${labels.date}: ${dateStr}`,
            `${labels.time}: ${hourStr}`,
            `${labels.professional}: ${svc?.professional?.name ?? "-"}`,
          ].join("\n");
        })
        .join(divider);
    },
    [t, i18n, business]
  );

  /**
   * Para entradas de APPOINTMENT_LIMIT_MODAL_IDS, constrói o botão secundário
   * de WhatsApp dinamicamente usando o telefone do business, traduções e
   * detalhes dos serviços (quando disponíveis via extraContext).
   */
  const buildDynamicOverride = useCallback(
    (config, extraContext = null) => {
      if (!APPOINTMENT_LIMIT_MODAL_IDS.includes(config?.id)) return null;
      const phone = business?.phone?.replace(/\D/g, "");
      const baseMessage = t("appointmentLimitWhatsAppMessage");
      const servicesMessage = formatServicesInfo(extraContext?.services);
      const includeUpgradeNote = config.id !== "professionalUnavailableReservation";
      const noteMessage = includeUpgradeNote
        ? t("appointmentNote", { upgradeLink: process.env.REACT_APP_UPGRADE_URL || "https://blink.prit.app/link/link_16" })
        : null;
      const fullMessage = servicesMessage
        ? `${baseMessage}\n\n${servicesMessage}${noteMessage ? `\n\n${noteMessage}` : ""}`
        : baseMessage;
      const text = encodeURIComponent(fullMessage);
      const url = phone ? `https://wa.me/${phone}?text=${text}` : `https://wa.me/?text=${text}`;
      return {
        secondaryButton: {
          labelKey: "notifyOnWhatsApp",
          action: () => {
            mixpanelTrackEvent(
              "Redirecionamento_WhatsApp_Iniciado",
              {
                motivo: config.id === "professionalUnavailableReservation" ? "profissional_indisponivel" : "limite_plano",
                valor_agendamento: extraContext?.valor_agendamento != null
                  ? Utils.formatMoney(extraContext.valor_agendamento / 100, business?.currency)
                  : undefined,
              },
              currentUser,
              business
            );
            window.open(url, "_blank");
          },
        },
      };
    },
    [business, currentUser, t, formatServicesInfo]
  );

  /**
   * Tenta mapear o erro para uma mensagem customizada.
   * Retorna true se exibiu o modal customizado, false se não houve match
   * (neste caso o caller deve usar o dialog genérico existente).
   */
  const showApiError = useCallback(
    (error, customChildren = null, actionsOverride = null, extraContext = null) => {
      const config = getApiErrorConfig(error);
      if (!config) return false;

      const dynamicOverride = buildDynamicOverride(config, extraContext);
      const merged = {
        // Caller override tem prioridade sobre o dynamic para o botão primário
        primaryButton: actionsOverride?.primaryButton ?? dynamicOverride?.primaryButton,
        // Dynamic override tem prioridade sobre o caller para o secundário:
        // o contexto conhece a ação canônica do erro (ex: WhatsApp para appointmentLimitPlan)
        secondaryButton: dynamicOverride?.secondaryButton ?? actionsOverride?.secondaryButton,
      };

      const primary = merged.primaryButton ?? config.primaryButton;
      const secondary = merged.secondaryButton ?? config.secondaryButton;

      const resolvedMessage = config.translateMessage === false
        ? extractApiMessage(error)
        : (config.messageKey ? t(config.messageKey) : (config.message ?? null));

      let primaryButton = primary
        ? { label: primary.labelKey ? t(primary.labelKey) : primary.label, onClick: resolveAction(primary.action ?? primary.onClick) }
        : null;

      if (config?.id === "appointmentLimitPlan") {
        mixpanelTrackEvent("Limite_Agendamentos_Atingido", {}, currentUser, business);

        if (primaryButton) {
          const originalOnClick = primaryButton.onClick;
          primaryButton = {
            ...primaryButton,
            onClick: () => {
              mixpanelTrackEvent(
                "Modal_Whatsapp_Dispensado",
                { valor_agendamento: extraContext?.valor_agendamento },
                currentUser,
                business
              );
              if (originalOnClick) originalOnClick();
            },
          };
        }
      }

      if (config?.id === "professionalUnavailableReservation") {
        mixpanelTrackEvent("Profissional_Indisponivel_Modal_Exibido", {}, currentUser, business);

        if (primaryButton) {
          const originalOnClick = primaryButton.onClick;
          primaryButton = {
            ...primaryButton,
            onClick: () => {
              mixpanelTrackEvent(
                "Profissional_Indisponivel_Modal_Dispensado",
                {},
                currentUser,
                business
              );
              if (originalOnClick) originalOnClick();
            },
          };
        }
      }

      setModal({
        open: true,
        title: config.titleKey ? t(config.titleKey) : config.title,
        message: resolvedMessage,
        children: customChildren,
        primaryButton,
        secondaryButton: secondary
          ? { label: secondary.labelKey ? t(secondary.labelKey) : secondary.label, onClick: resolveAction(secondary.action ?? secondary.onClick) }
          : null,
      });

      return true;
    },
    [buildDynamicOverride, business, currentUser, resolveAction, t]
  );

  /**
   * Abre o modal diretamente com configuração manual,
   * para casos onde o caller quer exibir o ModalError sem passar pelo mapa de erros.
   */
  const showError = useCallback(
    ({ title, children: customChildren = null, message = null, primaryButton = null, secondaryButton = null }) => {
      setModal({
        open: true,
        title,
        message,
        children: customChildren,
        primaryButton: primaryButton
          ? { label: primaryButton.label, onClick: resolveAction(primaryButton.action ?? primaryButton.onClick) }
          : null,
        secondaryButton: secondaryButton
          ? { label: secondaryButton.label, onClick: resolveAction(secondaryButton.action ?? secondaryButton.onClick) }
          : null,
      });
    },
    [resolveAction]
  );

  return (
    <ApiErrorModalContext.Provider value={{ showApiError, showError, hideError }}>
      {children}
      <ModalError
        open={modal.open}
        onClose={hideError}
        title={modal.title}
        primaryButton={modal.primaryButton}
        secondaryButton={modal.secondaryButton}
      >
        {modal.children ?? (modal.message ? <span style={{ color: "#666666", fontSize: "clamp(14px, 2vw, 18px)", lineHeight: 1.5, display: "block", textAlign: "center", padding: "0 4px" }}>{modal.message}</span> : null)}
      </ModalError>
    </ApiErrorModalContext.Provider>
  );
}

export function useApiErrorModal() {
  const ctx = useContext(ApiErrorModalContext);
  if (!ctx) throw new Error("useApiErrorModal must be used within ApiErrorModalProvider");
  return ctx;
}
